import React from "react";
import { Panel } from "@/components/portal/crm/kit";
import { born, day, money } from "@/lib/companyCheck";

const ROWS = [
  ["Turnover", "turnover"], ["Gross profit", "gross_profit"], ["Operating profit", "operating_profit"],
  ["Profit before tax", "profit_before_tax"], ["Profit after tax", "profit_after_tax"], ["Cash at bank", "cash"],
  ["Stock", "stock"], ["Net worth", "net_worth"], ["Working capital", "working_capital"], ["Total assets", "total_assets"],
  ["Total liabilities", "total_liabilities"], ["Bank loans & overdrafts", "bank_loans"], ["Director loans", "director_loans"],
];

const DOC_FIGS = [
  ["Turnover", "turnover"], ["Gross profit", "gross_profit"], ["Operating profit", "operating_profit"],
  ["Profit before tax", "profit_before_tax"], ["Net assets", "net_assets"], ["Total assets", "total_assets"],
  ["Cash", "cash"], ["Stock", "stock"], ["Paid in (total)", "money_in_total"], ["Paid in a month (average)", "money_in_monthly_avg"],
];

/** What Connie read from each document the customer uploaded — every figure says which document it came from. */
export function DocFigures({ docs, testId }) {
  const items = docs?.items || [];
  if (!items.length) {
    return <p className="sec-sub" data-testid={testId}>{docs?.reading ? `Connie is reading ${docs.reading} document${docs.reading === 1 ? "" : "s"} — the figures land here in a minute.`
      : "Nothing read yet. When the customer uploads accounts or bank statements, Connie reads them and the figures appear here."}</p>;
  }
  return (
    <div data-testid={testId}>
      <ul className="cc-list">
        {items.map((it) => {
          const f = it.figures || {};
          const figs = DOC_FIGS.filter(([, k]) => f[k] != null);
          const when = [f.period_start && day(f.period_start), f.period_end && day(f.period_end)].filter(Boolean).join(" – ");
          return (
            <li key={it.file_id} className="cc-doc" data-testid={`${testId}-${it.file_id}`}>
              <span>
                <b>{it.kind_label}{f.bank_name ? ` · ${f.bank_name}` : ""}</b>
                <small>{it.filename}{when ? ` · ${when}` : ""}{f.months_covered ? ` · ${Math.round(f.months_covered)} months` : ""}{f.employees ? ` · ${f.employees} staff` : ""}</small>
                {figs.length > 0 && <span className="cc-doc-figs">{figs.map(([l, k]) => <span key={k}>{l} <b>{money(f[k])}</b></span>)}</span>}
                {(f.notes || []).length > 0 && <small className="cc-doc-notes">{f.notes.join(" · ")}</small>}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="sec-sub" style={{ marginTop: 8 }}>Read by Connie from documents the customer uploaded — worth a glance at the document before relying on a figure.</p>
    </div>
  );
}

const OWED = new Set(["total_liabilities", "bank_loans", "director_loans"]);  // filed as negatives — shown as what is owed

export function Accounts({ rows, testId, filing }) {
  if (!rows?.length) return <p className="sec-sub" data-testid={testId}>No filed accounts on record.</p>;
  const shown = ROWS.filter(([, k]) => rows.some((r) => r[k] != null));
  const cell = (r, k) => (OWED.has(k) && r[k] != null ? Math.abs(r[k]) : r[k]);
  const gaps = shown.some(([, k]) => rows.some((r) => r[k] == null));
  const stale = (Date.now() - new Date(rows[0].period).getTime()) / 864e5 > 548;
  return (
    <div className="cc-scroll" data-testid={testId}>
      {stale && (
        <p className="cc-note" style={{ marginTop: 0, marginBottom: 10 }} data-testid={`${testId}-stale`}>
          <b>These are the latest accounts at Companies House, to {day(rows[0].period)}.</b>{" "}
          {filing?.accounts_overdue ? `The company hasn't filed since — accounts were due ${day(filing.accounts_next_due)} — ` : "Nothing newer has been filed, "}
          so the public record can't show today's position. The customer's management accounts and recent bank statements are the up-to-date picture — Connie reads them as soon as they're uploaded.
        </p>
      )}
      <table className="cc-tbl">
        <thead><tr><th>Accounts to</th>{rows.map((r) => <th key={r.period}>{day(r.period)}</th>)}</tr></thead>
        <tbody>
          {shown.map(([label, k]) => (
            <tr key={k}><td>{label}</td>{rows.map((r) => <td key={r.period} className={cell(r, k) < 0 ? "neg" : ""}>{money(cell(r, k))}</td>)}</tr>
          ))}
          {rows.some((r) => r.employees) && <tr><td>Employees</td>{rows.map((r) => <td key={r.period}>{r.employees != null ? Number(r.employees).toLocaleString("en-GB") : "—"}</td>)}</tr>}
        </tbody>
      </table>
      {rows[0].pl_filed === false && (
        <p className="cc-note" data-testid={`${testId}-no-pl`}>
          <b>Profit &amp; loss not filed.</b> Small companies can keep it off the public record (s444 Companies Act 2006),
          so turnover and profit aren't published — that's why they're not shown, not because they're zero. Ask the customer for full or management accounts.
        </p>
      )}
      {gaps && <p className="sec-sub" style={{ marginTop: 8 }}>— means that line isn't shown in the filed accounts.</p>}
    </div>
  );
}

const BANDS = [[81, 99, "Secure", "#0B6B4F"], [61, 80, "Stable", "#2E9E6B"], [41, 60, "Normal", "#0055FF"],
  [21, 40, "Caution", "#E39A00"], [0, 20, "High risk", "#C8151F"]];

/** Who grades the score and how — the same words on the card's pop-over and in the full report. */
export function CreditGrading({ score, testId }) {
  return (
    <div className="cc-grade" data-testid={testId}>
      <div className="cc-grade-h">How the credit score is graded</div>
      <p>A data provider's own business risk model (the one behind Connexis Search): how likely the company is to fail in
        the next 12 months. It's worked out automatically, mostly from public records: filed accounts, county court
        judgments, filing history, company age and the directors' track record.</p>
      <ul>
        {BANDS.map(([lo, hi, name, c]) => (
          <li key={name} className={score != null && score >= lo && score <= hi ? "on" : ""}>
            <i style={{ background: c }} /><b>{lo}–{hi}</b>{name}
          </li>
        ))}
      </ul>
      <p className="cc-grade-foot">Not set by Connie or the desk, and not the bureau check lenders decide on (Experian,
        Creditsafe, Dun &amp; Bradstreet). Small firms with thin filed accounts tend to score low.</p>
    </div>
  );
}

export function Credit({ rows, testId }) {
  if (!rows?.length) return <p className="sec-sub" data-testid={testId}>No credit score on record.</p>;
  return (
    <>
      <table className="cc-tbl" data-testid={testId}>
        <thead><tr><th>Date</th><th>Score</th><th>Status</th></tr></thead>
        <tbody>{rows.map((c) => (
          <tr key={c.date}><td>{day(c.date)}</td><td>{c.score != null ? `${c.score}/${c.max || 99}` : "—"}</td><td>{c.status || "—"}</td></tr>
        ))}</tbody>
      </table>
      <div className="cc-grade-box"><CreditGrading score={rows[0].score} testId={`${testId}-grading`} /></div>
    </>
  );
}

export function Ccjs({ ccj, testId }) {
  if (!ccj) return <p className="sec-sub" data-testid={testId}>Not available.</p>;
  if (!ccj.total) return <p className="sec-sub" data-testid={testId}>None in the last 6 years.</p>;
  return (
    <ul className="cc-list" data-testid={testId}>
      {ccj.items.map((x, i) => (
        <li key={i}>
          <span><b>{money(x.amount)}</b> · {x.status || "—"}<small>{x.court || "Court"} · case {x.case_number || "—"}{x.satisfied ? ` · satisfied ${day(x.satisfied)}` : ""}</small></span>
          <span className="r">{day(x.date)}</span>
        </li>
      ))}
    </ul>
  );
}

const nation = (n) => n && n.split(",").map((p) => p.trim().replace(/\b\w/g, (c) => c.toUpperCase())).filter(Boolean).join(", ");

export function People({ directors, resigned, testId }) {
  return (
    <ul className="cc-list" data-testid={testId}>
      {(directors || []).map((d, i) => (
        <li key={i}>
          <span><b>{d.name}</b><small>{[d.role, d.occupation, nation(d.nationality), born(d.born) && `born ${born(d.born)}`].filter(Boolean).join(" · ")}</small></span>
          <span className="r">since {day(d.appointed)}</span>
        </li>
      ))}
      {(resigned || []).map((d, i) => (
        <li key={`r${i}`}><span><b>{d.name}</b><small>{d.role} · resigned</small></span><span className="r">{day(d.resigned)}</span></li>
      ))}
      {!directors?.length && !resigned?.length && <li><span className="sec-sub">No officers on record.</span></li>}
    </ul>
  );
}

export function Owners({ sh, group, testId }) {
  return (
    <div data-testid={testId}>
      {sh?.items?.length ? (
        <ul className="cc-list">
          {sh.items.map((x, i) => (
            <li key={i}>
              <span><b>{x.name}</b><small>{[x.type, x.share_class].filter(Boolean).join(" · ")}</small></span>
              <span className="r">{x.pct != null ? `${x.pct}%` : "—"}<span className="cc-pct"><i style={{ width: `${Math.min(100, x.pct || 0)}%` }} /></span></span>
            </li>
          ))}
        </ul>
      ) : <p className="sec-sub" data-testid={`${testId}-none`}>{sh?.bulk
        ? "Too many shareholders to list one by one — Companies House holds them as a bulk register (usual for a listed or widely held company)."
        : "No shareholder return on record."}</p>}
      {sh?.confirmation_date && <p className="sec-sub" style={{ marginTop: 8 }}>As confirmed on {day(sh.confirmation_date)}.</p>}
      {group && (group.ultimate || group.parent || group.subsidiaries > 0) && (
        <p className="sec-sub" style={{ marginTop: 10 }} data-testid={`${testId}-group`}>
          {group.ultimate && <>Part of the <b>{group.ultimate.name}</b> group{group.ultimate.country ? ` (${group.ultimate.country})` : ""}. </>}
          {group.parent && group.parent.name !== group.ultimate?.name && <>Immediate parent: <b>{group.parent.name}</b>. </>}
          {group.subsidiaries > 0 && <>{group.subsidiaries} subsidiar{group.subsidiaries === 1 ? "y" : "ies"}{group.subsidiary_names?.length ? `: ${group.subsidiary_names.join(", ")}${group.subsidiaries > group.subsidiary_names.length ? ` and ${group.subsidiaries - group.subsidiary_names.length} more` : ""}` : ""}.</>}
        </p>
      )}
    </div>
  );
}

export function Filing({ r, testId }) {
  const f = r.filing || {};
  return (
    <div data-testid={testId}>
      <ul className="cc-list">
        <li><span><b>Accounts</b><small>{f.last_accounts_to ? `Last made up to ${day(f.last_accounts_to)}` : "No accounts filed yet"}{f.last_accounts_type ? ` · ${f.last_accounts_type}` : ""}</small></span>
          <span className="r" style={{ color: f.accounts_overdue ? "#A3121C" : undefined }}>{f.accounts_overdue ? "Overdue" : `Next due ${day(f.accounts_next_due)}`}</span></li>
        <li><span><b>Confirmation statement</b></span>
          <span className="r" style={{ color: f.confirmation_overdue ? "#A3121C" : undefined }}>{f.confirmation_overdue ? "Overdue" : `Next due ${day(f.confirmation_next_due)}`}</span></li>
        {(r.recent_filings || []).map((d, i) => (
          <li key={i}><span>{d.title}<small>{d.category}{d.type ? ` · ${d.type}` : ""}</small></span><span className="r">{day(d.date)}</span></li>
        ))}
      </ul>
    </div>
  );
}

export const Section = ({ title, sub, right, children, testId }) => <Panel title={title} sub={sub} right={right} testId={testId}>{children}</Panel>;
